<template>
  <div>
    <Header></Header>
    <div class="col-12 d-inline-flex pb-3">
      <div class="col-10 ms-3 overflow-visible me-3 pt-3">
        <div class="stripe-configuration">
          <h2>{{ editMode ? 'Edit' : 'Create' }} Channel</h2>
          <p v-if="loading" class="text-muted">Loading channel...</p>
          <div v-else-if="loadError" class="error-message">{{ loadError }}</div>
          <form v-else @submit.prevent="saveChanges">
            <div v-for="field in fields" :key="field.name" class="form-group">
              <label v-if="field.name !== 'channelId' || editMode" :for="field.name">{{ field.label }}:</label>
              <div v-if="field.name === 'eventIds'" :id="field.name" class="form-control-plaintext">
                <p v-if="!headerApp" class="text-muted mb-2">Select an application in the header to set its linked event.</p>
                <select v-else :id="field.name + '-select'" class="form-select linked-event-select" :style="{ width: '500px' }" :value="currentEventId" @change="setEvent($event.target.value)">
                  <option value="">No linked event</option>
                  <option v-for="event in headerEvents" :key="event.eventId" :value="String(event.eventId)">
                    {{ event.eventId }} - {{ event.firstMatcher }} VS {{ event.secondMatcher }}
                  </option>
                </select>
              </div>
              <input v-if="((field.name !== 'channelId') || (field.name === 'channelId' && editMode)) && (field.name !== 'eventIds') && (field.name !== 'status') && (field.name !== 'active') && field.name !== 'providersMetadata'"
                     :id="field.name" v-model="editedConfig[field.name]" :style="{ minWidth: '500px', width: editedConfig[field.name] ? `${String(editedConfig[field.name]).length * 10}px` : '100px' }"
                     type="text" class="form-control responsive-input" :disabled="(field.name === 'channelId') && editMode">
              <input class="checkbox" v-else-if="field.name === 'active' || field.name === 'status'"
                     type="checkbox" :id="field.name" v-model="editedConfig[field.name]">
              <textarea class="form-control" :style="{ width: '500px'}" rows="6" v-else-if="field.name === 'providersMetadata'" :id="field.name" v-model="editedConfig[field.name]"> </textarea>
              <!-- The calendar Sources dropdowns (CalMatches, Daily) only offer channels whose tags contain the calendar's sport. -->
              <small v-if="field.name === 'tags'" class="form-text text-muted d-block">
                For calendars, tags must contain the SPORT (ex: <strong>cricket</strong>) - otherwise the channel is not offered as a Source on the calendar's Matches page.
              </small>
            </div>
            <div v-if="error != ''" class="error-message">{{error}}</div>
            <br>
            <a role="button" class="procricket-btn" @click="saveChanges">{{ editMode ? 'Save Changes' : 'Create Channel' }}</a>
            <a role="button" class="ms-3 procricket-btn--reverse " @click="cancelEdit"> Cancel </a>
          </form>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import Header from '@/components/Header.vue'
import {Channel} from '@/model/models'
import AdminService from "@/services/admin-service";
import Cookies from "js-cookie";

export default defineComponent({
  name: 'ChannelEdit',
  components: {
    Header,
  },
  data () {
    return {
      ChannelsList: [],
      EventsList: [],
      eventChanged: false,
      selectedApp: '',
      // Filled from the loaded channel (edit) or left empty (create).
      editedConfig: {
        channelId : undefined,
        channelName : undefined,
        eventIds : [],
        caption : undefined,
        protectKey : undefined,
        tags : undefined,
        appName: undefined,
        edgePort: undefined,
        liveViews : undefined,
        totalViews : undefined,
        providersMetadata : undefined,
        orderNumber : undefined,
        status : false,
        active : false
      },
      editMode: false,
      loading: false,
      loadError: '',
      fields: [
        { name: "channelId", label: "channelId" },
        { name: "channelName", label: "channelName" },
        { name: "eventIds", label: "Linked events" },
        { name: "caption", label: "caption" },
        { name: "protectKey", label: "protectKey" },
        { name: "appName", label: "Stream App" },
        { name: "edgePort", label: "Stream Port" },
        { name: "tags", label: "tags" },
        { name: "liveViews", label: "liveViews" },
        { name: "totalViews", label: "totalViews" },
        { name: "providersMetadata", label: "providersMetadata" },
        { name: "orderNumber", label: "orderNumber" },
        { name: "status", label: "status" },
        { name: "active", label: "active" }
      ],
      error: ""
    }
  },
  computed: {
    // Header application, normalised; empty when unset or invalid ("NaN", "undefined", "null")
    headerApp() {
      const app = String(this.selectedApp || '').trim().toLowerCase();
      return ['nan', 'undefined', 'null'].includes(app) ? '' : app;
    },
    // Events of the header application (the dropdown options)
    headerEvents() {
      if (!this.headerApp) return [];
      return this.EventsList.filter(event => String(event.application || '').trim().toLowerCase() === this.headerApp);
    },
    // The header application's event currently linked to this channel ('' when none)
    currentEventId() {
      const linked = this.headerEvents.find(event => this.editedConfig.eventIds.includes(Number(event.eventId)));
      return linked ? String(linked.eventId) : '';
    }
  },
  created() {
    // The header decides the application (it saves the cookie and reloads the page, so a ?app= from an older link would be stale).
    // The link's ?app= is only used when the header has not set one; the URL is then brought in line with the result.
    const appCookie = Cookies.get('application');
    this.selectedApp = appCookie !== undefined ? appCookie : (this.$route.query.app || '');
    if ((this.$route.query.app || '') !== this.selectedApp) {
      const query = { ...this.$route.query };
      if (this.selectedApp) query.app = this.selectedApp; else delete query.app;
      this.$router.replace({ query });
    }
    if (this.$route.params.channelId) {
      this.editMode = true;
      this.loadChannel(Number(this.$route.params.channelId));
    }
    this.doLoadEvents()
  },
  methods: {
    // Only the id is in the URL: the channel is read fresh (live views, status ...) instead of trusting values carried over from the list.
    loadChannel(channelId) {
      this.loading = true;
      this.loadError = '';
      AdminService.getChannelsList().then((list) => {
        const channel = (list || []).find(c => Number(c.channelId) === channelId);
        if (!channel) {
          this.loadError = `Channel ${channelId} not found.`;
          return;
        }
        this.editedConfig = {
          channelId: channel.channelId,
          channelName: channel.channelName,
          eventIds: Array.isArray(channel.eventIds) ? channel.eventIds.map(Number) : [],
          caption: channel.caption,
          protectKey: channel.protectKey,
          tags: channel.tags,
          appName: channel.appName,
          edgePort: channel.edgePort,
          liveViews: channel.liveViews,
          totalViews: channel.totalViews,
          providersMetadata: channel.providersMetadata,
          orderNumber: channel.orderNumber,
          status: channel.status,
          active: channel.active
        };
      }).catch((error) => {
        this.loadError = 'Could not load the channel. ' + (error.message || '');
      }).finally(() => {
        this.loading = false;
      });
    },
    saveChanges() {
      const dataToSave = new Channel(
          this.editedConfig.channelId ,
          this.editedConfig.channelName,
          this.editedConfig.eventIds,
          this.editedConfig.caption,
          this.editedConfig.protectKey ,
          this.editedConfig.tags,
          this.editedConfig.appName,
          this.editedConfig.edgePort,
          this.editedConfig.liveViews ,
          this.editedConfig.totalViews,
          this.editedConfig.providersMetadata,
          this.editedConfig.orderNumber ,
          this.editedConfig.status,
          this.editedConfig.active
      );
      // The BE links one event per application: send it only when the dropdown was used
      if (this.eventChanged && this.headerApp) {
        dataToSave.application = this.selectedApp;
        dataToSave.eventId = this.currentEventId === '' ? null : Number(this.currentEventId);
      }

      if (this.editMode) {
        AdminService.updateChannel(dataToSave).then((res) => {
          alert('Insert complete');

          if (window.history.length > 1) {
            this.$router.back()
          } else {
            this.$router.push({ name: 'Channels' })
          }
        })
            .catch((error) => {
              if (error.response && error.response.data && error.response.data.message) {
                alert(error.response.data.message);
              }
            });
      } else {
        AdminService.insertChannel(dataToSave).then(() => {
          alert('Insert complete');

          this.$router.push({ name: 'Channels' });
        }).catch((error) => {
          this.error = error.message
        });
      }
    },
    doLoadEvents() {
      AdminService.getEventsList().then((data) => {
        this.EventsList = data;
      }).catch((error) => {
        this.error = error.message
      });
    },
    // Replaces the header application's link with the chosen event (saved as application + eventId)
    setEvent(value) {
      this.eventChanged = true;
      const headerIds = this.headerEvents.map(event => Number(event.eventId));
      const kept = this.editedConfig.eventIds.filter(id => !headerIds.includes(id));
      this.editedConfig.eventIds = value === '' ? kept : [...kept, Number(value)];
    },
    cancelEdit() {
      this.$router.back();
    }
  }
})
</script>
<style src="../../assets/style.css">
</style>
